import { useCallback, useEffect, useState } from 'react';
import { ApiError } from './api';

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Minimal data-fetching hook. Re-runs when any `deps` change. Tracks a mounted
 * flag so a slow response after unmount never sets state. `refreshMs` (optional)
 * polls on an interval.
 */
export function useAsync<T>(fn: () => Promise<T>, deps: ReadonlyArray<unknown>, refreshMs?: number): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let mounted = true;
    let timer: number | undefined;
    const run = async () => {
      try {
        const result = await fn();
        if (!mounted) return;
        setData(result);
        setError(null);
      } catch (e) {
        if (!mounted) return;
        setError(e instanceof ApiError ? `${e.code}: ${e.message}` : (e as Error).message);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    setLoading(true);
    void run();
    if (refreshMs) timer = window.setInterval(run, refreshMs);
    return () => {
      mounted = false;
      if (timer) clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce, refreshMs]);

  return { data, loading, error, reload };
}
